import { useAlertStore } from '@/stores/alertStore';
import type { AxiosError, AxiosInstance, AxiosResponse } from 'axios';
import { t, te } from '@/plugins/i18n';
import { useLogger } from '@/composables/useLogger';
import { LoggerLevel } from '@/constants/logLevel';
import type { AxiosErrorResponseData } from '../types';
import { parseArrayBuffer, parseBlob } from '@/utils/objectManipulation';
import { useAuthStore } from '@/stores/authStore';
import { ApiStatusErrorMessage, DEFAULT_ERROR_MESSAGE, ERROR_CODES, HttpStatus } from '@/constants/apiStatusErrorMessage';
import { LimitErrorMessageForApiUrl } from '../constants/errors';

export const applyErrorInterceptor = (axios: AxiosInstance) => {
  const { addErrorAlert, alertExists } = useAlertStore();

  axios.interceptors.response.use(
    (response) => handleSuccessResponse(response, addErrorAlert),
    (error) => handleErrorResponse(error, addErrorAlert, alertExists)
  );
};

function handleSuccessResponse(response: AxiosResponse, addErrorAlert: (message: string) => void): AxiosResponse {
  const { data } = response || {};
  const { payload } = data || {};

  if (payload) {
    processPayloadErrors(payload, addErrorAlert);
    processPayloadWarnings(payload, addErrorAlert);
  }

  return response;
}

function processPayloadErrors(payload: Record<string, unknown>, addErrorAlert: (message: string) => void): void {
  const error = payload.error as { code?: number; message?: string } | undefined;
  if (!error) {
    return;
  };

  const { log } = useLogger();
  const statusMessage = getTranslatedErrorMessage(error.code);
  const errorMessage = statusMessage || error.message || error.code || error;
  const errorLevel = determineLogLevel(statusMessage, error.message, error.code);

  log({ message: error, level: errorLevel, type: 'error' });
  addErrorAlert(errorMessage + '');
}

function processPayloadWarnings(payload: Record<string, unknown>, addErrorAlert: (message: string) => void): void {
  const warnings = payload.warnings as Array<{ code?: number; message?: string }> | undefined;
  if (!warnings || !Array.isArray(warnings) || !warnings.length) return;

  const errorMessage = processWarnings(warnings);
  if (errorMessage) {
    addErrorAlert(errorMessage);
  }
}

function processWarnings(warnings: Array<{ code?: number; message?: string }>): string {
  const { log } = useLogger();

  return warnings
    .map((item) => {
      const statusMessage = getTranslatedErrorMessage(item.code);
      const warningLevel = determineLogLevel(statusMessage, item.message, item.code);

      log({ message: item, level: warningLevel, type: 'warn' });
      return statusMessage || item.message || item.code || String(item);
    })
    .join('\n');
}

async function handleErrorResponse(
  error: AxiosError,
  addErrorAlert: (message: string) => void,
  alertExists: (message: string) => boolean
): Promise<never> {
  const { response } = error;
  const responseData = await getResponseData(response as AxiosResponse);

  if (response?.status === HttpStatus.VALIDATION_ERROR) {
    handleValidationError(responseData, addErrorAlert);
  }

  if (response?.status === HttpStatus.LOCKED) {
    handleLockedError(alertExists, addErrorAlert);
  }

  if (response?.status === HttpStatus.RATE_LIMIT) {
    handleRateLimitError(error.config?.url || '', addErrorAlert);
  }

  if (response?.status !== HttpStatus.VALIDATION_ERROR
    && response?.status !== HttpStatus.LOCKED
    && response?.status !== HttpStatus.RATE_LIMIT) {
    handleGenericError(response, responseData, error, addErrorAlert);
  }

  return Promise.reject(error);
}

function handleValidationError(
  responseData: AxiosErrorResponseData<null> | null,
  addErrorAlert: (message: string, title?: string) => void
): void {
  const { log } = useLogger();
  const errorMessages = processValidationErrors(responseData);

  log({
    message: { error: responseData },
    level: LoggerLevel.TREASURY_STAGING,
    type: 'error'
  });

  addErrorAlert(errorMessages.join('\n'), 'errors.someErrorsOccuredCheckListBelow');
}

function processValidationErrors(responseData: AxiosErrorResponseData<null> | null): string[] {
  if (!responseData?.errors) {
    return [];
  };

  return responseData.errors.map((error) => {
    const isSpecialError = ERROR_CODES.SPECIAL_FORMAT_ERRORS.some(
      (code) => code === error.code
    );

    if (isSpecialError) {
      const [errorCode] = error.message.split('-');
      return t('apiStatusCodes.' + errorCode);
    }
    return t('apiStatusCodes.' + error.code);
  });
}

function handleLockedError(
  alertExists: (message: string) => boolean,
  addErrorAlert: (message: string) => void
): void {
  if (!alertExists(ApiStatusErrorMessage[HttpStatus.LOCKED])) {
    addErrorAlert(ApiStatusErrorMessage[HttpStatus.LOCKED]);
  }
  useAuthStore().onLogout();
}

function handleRateLimitError(url: string, addErrorAlert: (message: string) => void): void {
  const limitErrorMessage = LimitErrorMessageForApiUrl[url as keyof typeof LimitErrorMessageForApiUrl];
  if (limitErrorMessage) {
    addErrorAlert(limitErrorMessage);
  } else {
    addErrorAlert(ApiStatusErrorMessage[HttpStatus.RATE_LIMIT]);
  }
}

function handleGenericError(
  response: AxiosResponse | undefined,
  responseData: AxiosErrorResponseData<null> | null,
  error: AxiosError,
  addErrorAlert: (message: string) => void
): void {
  const { log } = useLogger();
  const errorStatus = !response
    ? error.request?.status
    : responseData?.status?.code || response.status;

  const statusMessage = getTranslatedErrorMessage(errorStatus);

  let errorMessage: string = statusMessage
    || responseData?.message
    || (responseData?.code ? responseData.code + '' : '')
    || DEFAULT_ERROR_MESSAGE;

  const errorLevel = determineLogLevel(statusMessage, responseData?.message, responseData?.code);

  log({ message: error, level: errorLevel, type: 'error' });

  if (responseData?.additionalInformation) {
    errorMessage += ' : ' + responseData.additionalInformation;
  }

  addErrorAlert(errorMessage);
}

function getTranslatedErrorMessage(errorCode: string | number | undefined): string {
  if (errorCode === undefined) {
    return '';
  };

  return te(`apiStatusCodes.${errorCode}`)
    ? t(`apiStatusCodes.${errorCode}`)
    : '';
}

function determineLogLevel(
  statusMessage: string,
  errorMessage: string | undefined,
  errorCode: string | number | undefined
): LoggerLevel {
  return !statusMessage && !errorMessage && !errorCode
    ? LoggerLevel.PRODUCTION
    : LoggerLevel.TREASURY_STAGING;
}

async function getResponseData(response?: AxiosResponse): Promise<AxiosErrorResponseData<null> | null> {
  if (!response?.data) {
    return null;
  }

  const { data: responseData, headers } = response;
  const contentType = headers['content-type'] || '';

  if (!contentType.includes('application/json')) {
    return responseData as AxiosErrorResponseData<null> | null;
  }

  if (responseData instanceof ArrayBuffer) {
    return parseArrayBuffer<AxiosErrorResponseData<null>>(responseData);
  }

  if (responseData instanceof Blob) {
    return parseBlob<AxiosErrorResponseData<null>>(responseData);
  }

  return responseData as AxiosErrorResponseData<null> | null;
}
